import { useQuery } from '@tanstack/react-query';
import { get } from 'api/base';
import { readWorkstationName } from 'utils/workstation';

interface OpenedPatientResponse {
  patientId: string | null;
}

/**
 * The patient opened at this machine in the last 15 minutes according to medatixx, or null.
 *
 * Null for every reason there is; the caller asks for the id by hand in all of them, and why the
 * answer was missing stays in the facade's own log.
 *
 * VITE_WORKSTATION_NAME overrides the machine name for testing; outside Electron without it the question is never put.
 */
export function useOpenedPatient(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['openedPatient'],
    queryFn: async (): Promise<string | null> => {
      const workstation = await readWorkstationName();
      if (!workstation) {
        return null;
      }
      const answer = (await get('openedPatient', undefined, {
        workstation,
      })) as OpenedPatientResponse | null;
      return answer?.patientId ?? null;
    },
    enabled: options?.enabled ?? true,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}
